import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { LoginActions } from 'Redux/modules/login/actions';
import { LoginSelectors } from 'Redux/modules/login/selectors';
import AppConfig from 'Config/AppConfig';
import AuthFooter from '../../../Components/AuthFooter/AuthFooter';
import PiplLogo from '../../../Assets/pipl_80_49.svg';
import googleIcon from '../../../Assets/google_icon.png';
import showPasswordIcon from '../../../Assets/show-password.svg';
import hidePasswordIcon from '../../../Assets/hide-password.svg';
import styles from './LoginPage.module.less';

// Derive the Google SSO URL.  In production the Django template fills this in;
// here we point to the well-known allauth endpoint.  A real next param is
// forwarded when present in the current URL.
function googleSsoHref() {
  const next = new URLSearchParams(window.location.search).get('next');
  const base = `/${AppConfig.ACCOUNTS_PREFIX}auth/login/google-oauth2/`;
  return next ? `${base}?next=${encodeURIComponent(next)}` : base;
}

function ssoHref() {
  const next = new URLSearchParams(window.location.search).get('next');
  const base = `/${AppConfig.ACCOUNTS_PREFIX}login-with-sso/`;
  return next ? `${base}?next=${encodeURIComponent(next)}` : base;
}

const initialMessages = (window.__INITIAL_STATE && window.__INITIAL_STATE.messages) || [];
// Server-seeded (LoginView context): only show the sign-up CTA when the server
// would have, and point it at the purchase or trial flow like the old template.
const showSignUpView = !!(window.__INITIAL_STATE && window.__INITIAL_STATE.show_sign_up_view);
const signUpPath =
  window.__INITIAL_STATE && window.__INITIAL_STATE.search_next_page_is_purchase
    ? 'sign-up-search'
    : 'trial-sign-up-search';

const LoginPage = () => {
  const dispatch = useDispatch();

  const reduxIsSubmitting = useSelector(LoginSelectors.loginIsSubmittingSelector);
  const error = useSelector(LoginSelectors.loginErrorSelector);
  const requiresCaptcha = useSelector(LoginSelectors.loginRequiresCaptchaSelector);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [localIsSubmitting, setLocalIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const captchaRef = useRef(null);

  // Reset local lock when redux signals the request is done (success or failure).
  useEffect(() => {
    if (!reduxIsSubmitting) setLocalIsSubmitting(false);
  }, [reduxIsSubmitting]);

  const isSubmitting = localIsSubmitting || reduxIsSubmitting;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setLocalIsSubmitting(true);
    const next = new URLSearchParams(window.location.search).get('next');
    // Fall back to the live input values so password-manager autofill (which can
    // skip React's onChange) is still picked up.
    const emailValue = email || e.target.elements.email?.value || '';
    const passwordValue = password || e.target.elements.password?.value || '';
    // deviceToken will be wired to the DI SDK in a later PR; include key now for forward-compat.
    dispatch(
      LoginActions.submitLogin({
        email: emailValue,
        password: passwordValue,
        remember,
        captcha: captchaToken,
        deviceToken: null,
        next,
      }),
    );
  };

  const isAutomation = typeof document !== 'undefined' && document.cookie.indexOf('automation_tests') > -1;

  return (
    <>
      <div className={styles['center-form']}>
        <Helmet>
          <title>Log in to Pipl</title>
        </Helmet>
        <a href="https://pipl.com">
          <img className={`center-block ${styles.logo}`} src={PiplLogo} alt="Pipl" width="80" height="49" />
        </a>
        <div className={`${styles.loginHeader} loginHeader`}>Log in to Pipl</div>

        <form id="login-form" onSubmit={handleSubmit} autoCapitalize="off" autoCorrect="off">
          {/* Email — custom input + floating label (ported from login.less) for exact parity */}
          <div className={styles['form-group']}>
            <input
              id="id_email"
              name="email"
              type="email"
              maxLength={255}
              className={`${styles['form-control']} ${styles.emailInput} ${email ? styles.green : ''}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setEmailFocused(true)}
              onBlur={() => setEmailFocused(false)}
              required
            />
            <label
              htmlFor="id_email"
              className={`${styles['floating-label']} ${emailFocused || email ? styles['floating-label-active'] : ''} ${
                email ? styles.green : ''
              }`}
            >
              Email
            </label>
            <span id="required-email-msg" hidden className="text-danger warning">
              This field is required
            </span>
            <span id="invalid-email-msg" hidden className="text-danger warning">
              Please enter a valid Email
            </span>
          </div>

          {/* Password — custom input + floating label + the original show/hide eye icons */}
          <div className={styles['form-group']} id="hidePassword">
            <input
              id="id_password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              className={`${styles['form-control']} ${styles.passwordInput} ${password ? styles.green : ''}`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              required
            />
            <label
              htmlFor="id_password"
              className={`${styles['floating-label']} ${
                passwordFocused || password ? styles['floating-label-active'] : ''
              } ${password ? styles.green : ''}`}
            >
              Password
            </label>
            <button
              type="button"
              className={styles.eyeBtn}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((v) => !v)}
            >
              <img src={showPassword ? hidePasswordIcon : showPasswordIcon} alt="" width="20" height="20" />
            </button>
            <span id="invalid-password-msg" hidden className="text-danger warning">
              This field is required
            </span>
          </div>

          {/* reCAPTCHA — rendered only when backend signals it is required */}
          {requiresCaptcha && !isAutomation && (
            <div className={styles.captcha}>
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={AppConfig.RECAPTCHA_KEY || (window.__CONFIG__ && window.__CONFIG__.RECAPTCHA_KEY) || ''}
                onChange={(token) => setCaptchaToken(token)}
              />
            </div>
          )}

          {/* Server-seeded messages (e.g. "Sorry, your account is inactive.") */}
          {initialMessages.map((m, i) => (
            <div key={i} id="error-div" className={`${styles['form-group']} ${styles['text-danger']}`}>
              <span>{m.text}</span>
            </div>
          ))}

          {/* Error display */}
          {error && (
            <div id="invalid-credentials-msg" className={`${styles['form-group']} ${styles['text-danger']}`}>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            id="submit-id-login"
            className={`${styles.loginButton} ${styles.btn}`}
            disabled={isSubmitting}
          >
            Login
          </button>

          {/* Remember me + Forgot password */}
          <div id="login-options" className={`${styles['form-group']} ${styles.idRemember}`}>
            <div className={styles.checkbox}>
              <label className={styles.checkboxContainer}>
                <span className="CheckboxLabel">
                  <small className={styles.rememberLabel} id="rememberText">
                    Remember Me
                  </small>
                </span>
                <input
                  id="id_remember"
                  name="remember"
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span className={styles.checkmark}></span>

                <a id="reset-password" href={`/${AppConfig.ACCOUNTS_PREFIX}password/reset/`} className="pull-right">
                  <small className={styles.small}>Forgot password?</small>
                </a>
              </label>
            </div>
          </div>

          {/* SSO options */}
          <div className={styles['sign-up-link-wrapper']}>
            <div className={styles.alreadyHaveAccountHeader}>
              <div className={styles.newToPiplBar}>
                <div className={styles.titleBar}></div>
                <div className={styles.singleSignOn}>Use single sign-on authentication</div>
              </div>
              <a href={googleSsoHref()} className={`${styles['auth-button-box']} ${styles.btn}`}>
                <img src={googleIcon} width="18" alt="Google" style={{ marginRight: 10 }} />
                Log in with Google
              </a>
              <a id="oidc_auth" href={ssoHref()} className={`${styles['auth-button-box']} ${styles.btn}`}>
                Log in with your organization
              </a>
            </div>
          </div>

          {/* Sign-up link — only when the server would have shown it */}
          {showSignUpView && (
            <div className={styles['sign-up-link-wrapper']}>
              <div className={styles.alreadyHaveAccountHeader}>
                <div className={styles.newToPiplBar}>
                  <div className={styles.titleBar}></div>
                  <div className={styles.newToPipl}>New to Pipl?</div>
                </div>
                <a className={styles.signinLink} href={`/${AppConfig.ACCOUNTS_PREFIX}${signUpPath}`}>
                  <span>Create your Pipl account</span>
                </a>
              </div>
            </div>
          )}
        </form>
      </div>
      <AuthFooter />
    </>
  );
};

export default LoginPage;
